/**
 * CORE-1428 — что делает кнопка на карточке «Ожидаемых событий».
 *
 * Логика жила прямо в разметке и сводила вместе два разных смысла:
 * «нельзя записаться ЭТОЙ кнопкой» и «событие недоступно». У комнаты
 * `canRsvp=false` стоит намеренно (CORE-1286) — её «иду» это подписка на
 * комнату, а не запись в календарь Lives. Вошедший пользователь получал из
 * этого «Недоступно» и мёртвую кнопку на публичной запланированной встрече,
 * которая прекрасно открывается и предлагает «Иду» на своей странице.
 *
 * Отдельной функцией — чтобы матрицу «гость / вошёл / уже идёт» × «комната /
 * лайв» можно было проверить целиком, а не по одному примеру из отчёта.
 */

/** Что показать и что сделать по нажатию. */
export type UpcomingCardAction =
  /** Кнопка гасится: записаться правда нельзя. */
  | { kind: 'unavailable' }
  /** Человек уже идёт — остаётся сказать об этом. */
  | { kind: 'going' }
  /** Обычная запись через календарь Lives. */
  | { kind: 'rsvp' }
  /** Комната: ведём туда, где «Иду» живёт. */
  | { kind: 'open-room'; code: string };

export interface UpcomingCardInput {
  /** `/room/uTHIkH` у комнаты, `/lives/154` у лайва. */
  detailsUrl?: string | null;
  canRsvp?: boolean | null;
  authRequired?: boolean | null;
  isAuthenticated: boolean;
  /** Уже идёт: `isAttending` с сервера либо локальное состояние после нажатия. */
  isGoing: boolean;
}

/** Код комнаты из ссылки карточки, иначе `null`. */
export function roomCodeFromDetailsUrl(detailsUrl?: string | null): string | null {
  const m = /^\/room\/([A-Za-z0-9_-]+)/.exec((detailsUrl || '').trim());
  return m ? m[1] : null;
}

export function resolveUpcomingCardAction(input: UpcomingCardInput): UpcomingCardAction {
  const { canRsvp, authRequired, isAuthenticated, isGoing } = input;
  const code = roomCodeFromDetailsUrl(input.detailsUrl);

  // «Уже идёт» старше всего остального: это факт, а не разрешение.
  if (isGoing) return { kind: 'going' };

  // Комната не умеет RSVP с этой поверхности — и не должна притворяться
  // недоступной. Гостя кнопка доведёт до входа, вошедшего — до самой комнаты.
  if (code) return { kind: 'open-room', code };

  // Гость видит рабочую кнопку: `canRsvp` у него ложно просто потому, что он
  // не вошёл, и гасить её означало бы закрыть дорогу к регистрации.
  if (!isAuthenticated && authRequired) return { kind: 'rsvp' };

  return canRsvp === false ? { kind: 'unavailable' } : { kind: 'rsvp' };
}
